// Finding a place by name, for the editors that need a point rather than a footprint.
//
// The area editor searches for a site's published outline, because an area is a shape. A checkpoint
// is not: a gate, a weighbridge or a bay is somewhere you stand, and what the editor needs is to be
// looking at the right few hundred metres of ground. So this returns a point and the viewport around
// it, and leaves outlines to the editor that draws them.
//
// Two providers, because neither alone is enough: OpenStreetMap knows industrial sites by name and
// publishes where they are, and Google finds almost anything else - an address, a village, a
// landmark somebody navigates by. Both are asked, and both sets are offered, because the person
// reading the list knows which one is their plant and no ranking here can.

// The words that belong to a registrar rather than to a place. "TATA Steel Ltd Meramandali" finds
// nothing on OpenStreetMap; "TATA Steel Meramandali" finds the works. Dropped on a second attempt
// rather than the first, so an exact name that does match is never spoiled.
const LEGAL_FORM = /\b(ltd|limited|pvt|private|inc|incorporated|corp|corporation|co|llp|plc|gmbh|sa|bv)\b\.?/gi;

const box = (row) => {
  const edges = (row.boundingbox || []).map(Number);
  return edges.length === 4 && edges.every(Number.isFinite)
    ? { south: edges[0], north: edges[1], west: edges[2], east: edges[3] }
    : null;
};

const openStreetMap = async (text) => {
  const url = `https://nominatim.openstreetmap.org/search?${new URLSearchParams({
    q: text, format: 'jsonv2', limit: '5', addressdetails: '1',
  })}`;
  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('OpenStreetMap search failed.');
  return response.json();
};

/**
 * Places matching what somebody typed, from both providers, as points.
 *
 * `maps` is the loaded Google Maps namespace where one is available; without it the search still
 * works on OpenStreetMap alone rather than failing, because a plant that cannot reach Google should
 * still be able to find itself.
 *
 * A provider that fails is left out rather than taking the search down with it. One empty list is a
 * worse answer than a short one, and the person can see which source each result came from.
 */
export const findPlaces = async (query, maps) => {
  const text = (query || '').trim();
  if (text.length < 3) return [];

  const fromOsm = (async () => {
    let rows = await openStreetMap(text);
    const plainer = text.replace(LEGAL_FORM, ' ').replace(/\s+/g, ' ').trim();
    if (!rows.length && plainer && plainer !== text) rows = await openStreetMap(plainer);
    return rows.map((row) => ({
      key: `osm-${row.osm_type}-${row.osm_id}`,
      source: 'OpenStreetMap',
      label: row.display_name,
      name: (row.name || row.display_name || '').split(',')[0],
      latitude: Number(row.lat),
      longitude: Number(row.lon),
      bounds: box(row),
    }));
  })();

  const fromGoogle = (async () => {
    if (!maps) return [];
    const { Place } = await maps.importLibrary('places');
    const { places } = await Place.searchByText({
      textQuery: text,
      fields: ['displayName', 'formattedAddress', 'location', 'viewport'],
      maxResultCount: 5,
    });
    return (places || []).map((place, index) => ({
      key: `google-${index}-${place.location?.lat()}-${place.location?.lng()}`,
      source: 'Google',
      label: place.formattedAddress || place.displayName,
      name: place.displayName || '',
      latitude: place.location?.lat(),
      longitude: place.location?.lng(),
      bounds: place.viewport
        ? {
          south: place.viewport.getSouthWest().lat(),
          west: place.viewport.getSouthWest().lng(),
          north: place.viewport.getNorthEast().lat(),
          east: place.viewport.getNorthEast().lng(),
        }
        : null,
    }));
  })();

  const found = await Promise.allSettled([fromOsm, fromGoogle]);
  return found
    .filter((outcome) => outcome.status === 'fulfilled')
    .flatMap((outcome) => outcome.value)
    .filter((place) => Number.isFinite(place.latitude) && Number.isFinite(place.longitude));
};

export default findPlaces;
